/**
 * Profile Component
 * 
 * A comprehensive user profile management interface that allows users to:
 * - View their profile information including username and join date
 * - Edit their username with real-time updates
 * - Manage their account settings
 * - Sign out of their account
 * 
 * The component integrates with Supabase for data persistence and authentication,
 * providing immediate feedback through toast notifications for all user actions.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LogOut, User, Pencil, Check, X } from "lucide-react";

const Profile = () => {
  // Navigation hook for programmatic routing after actions like sign out
  const navigate = useNavigate();
  // Toast hook for displaying user feedback notifications
  const { toast } = useToast();
  
  // State Management
  // loading: Tracks async operations to show loading states
  const [loading, setLoading] = useState(false);
  // profile: Stores the user's profile data from Supabase
  const [profile, setProfile] = useState<any>(null);
  // isEditing: Controls the visibility of the username edit form
  const [isEditing, setIsEditing] = useState(false);
  // newUsername: Temporary storage for username being edited
  const [newUsername, setNewUsername] = useState("");

  /**
   * Profile Data Fetching Effect
   * 
   * Runs on component mount to retrieve the user's profile information.
   * Process:
   * 1. Gets the current authenticated user from Supabase auth
   * 2. Fetches the corresponding profile data from the profiles table
   * 3. Updates local state with the retrieved data
   * 4. Handles any errors that occur during the process
   */
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Retrieve current authenticated user's data
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("No user found");

        // Query the profiles table for user-specific data
        const { data: profileData, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;

        // Log successful data retrieval for debugging
        console.log("Profile data fetched:", profileData);
        
        // Update local state with retrieved profile data
        setProfile(profileData);
        setNewUsername(profileData.username || "");
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Display error notification to user
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load profile data",
        });
      }
    };

    // Execute the fetch operation
    fetchProfile();
  }, [toast]); // Re-run if toast utility changes

  /**
   * Username Update Handler
   * 
   * Manages the process of updating a user's username:
   * 1. Validates current user authentication
   * 2. Updates the username in Supabase
   * 3. Updates local state to reflect changes
   * 4. Provides user feedback via toast notifications
   * 
   * Error handling includes:
   * - Authentication validation
   * - Database update errors
   * - Network issues
   */
  const handleUpdateUsername = async () => {
    try {
      setLoading(true);
      // Verify user is still authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Perform the username update in Supabase
      const { error } = await supabase
        .from("profiles")
        .update({ username: newUsername })
        .eq("id", user.id);

      if (error) throw error;

      // Update local state and UI
      setProfile({ ...profile, username: newUsername });
      setIsEditing(false);
      // Notify user of successful update
      toast({
        title: "Success",
        description: "Username updated successfully",
      });
    } catch (error) {
      console.error("Error updating username:", error);
      // Notify user of update failure
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update username",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign Out Handler
   * 
   * Manages the user sign out process:
   * 1. Calls Supabase auth signOut method
   * 2. Clears local session data
   * 3. Redirects to login page
   * 4. Provides feedback via toast notifications
   */
  const handleSignOut = async () => {
    try {
      // Attempt to sign out user
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Notify user and redirect to login
      toast({
        title: "Success",
        description: "You have been signed out successfully",
      });
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      // Notify user of sign out failure
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to sign out",
      });
    }
  };

  // Render user interface
  return (
    <div className="container max-w-2xl mx-auto py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Manage your account settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Profile Avatar and Username Section */}
          <div className="flex flex-col items-center space-y-4">
            {/* User Avatar Display */}
            <Avatar className="h-24 w-24">
              <AvatarImage src={profile?.avatar_url} />
              <AvatarFallback>
                <User className="h-12 w-12" />
              </AvatarFallback>
            </Avatar>

            {/* Username Management Section */}
            <div className="space-y-2 text-center">
              {isEditing ? (
                // Username Edit Form - Shown when isEditing is true
                <div className="flex items-center space-x-2">
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Enter new username"
                    className="max-w-[200px]"
                  />
                  {/* Save Changes Button */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleUpdateUsername}
                    disabled={loading}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  {/* Cancel Edit Button */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      setNewUsername(profile?.username || "");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                // Username Display - Shown when not editing
                <div className="flex items-center justify-center space-x-2">
                  <h3 className="text-xl font-semibold">
                    {profile?.username || "Anonymous User"}
                  </h3>
                  {/* Edit Username Button */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {/* Account Creation Date Display */}
              <p className="text-sm text-muted-foreground">
                Member since {new Date(profile?.created_at || "").toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Account Management Actions */}
          <div className="flex justify-center pt-4">
            {/* Sign Out Button */}
            <Button
              variant="destructive"
              onClick={handleSignOut}
              className="w-full max-w-[200px]"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;